// KPIs › Scorecard (`/c/{slug}/kpis/scorecard`; KPIS-MODE, founder-approved 2026-10-22, after
// HOME-CAD-UX 2026-10-21): the company's scorecard as EOS / Ninety.io show it. A 56 px location
// header, the period's four tiles from the company roll-up, then the Scorecard · Reports tabs. The
// scorecard is one grid (`ScorecardGrid`): the company's own KPIs, then the seats grouped by team
// (each team's own KPIs one click away), × the last 13 weeks, months or quarters, with in-place
// entry, corrections with a reason, and each cell's history.
//
// Filters (all in the URL, scorecard-search.ts): Team (the focus scope `scope=team:{id}`), Seat
// (the focus subject `s=seat:{id}`; a DE's seat for `s=de:{id}`), "With their branch" (the seat
// and everyone under it), "Mine" (the seats the viewer holds), "Off track" (only the KPI rows
// missed or near this period), and the columns (`cols=month|quarter`). `kpi={id}` focuses that
// KPI's current cell on load. Only the seats in the server's `kpi_visible_seat_ids` show (a
// client viewer, refused there, sees what the company card already cut to its view).
//
// Who enters what: a manager every seat's manual and imported KPIs; a seat's person holder its own
// seats' (MEMBER-ENTRY; a correction needs a reason). `scoped` (a plain member, not a company KPI
// reader): no company card (403), tiles, Reports or team scorecards — the grid is built from one
// `seats.scorecard` per visible seat (its own; a supervisor's branch).
//
// Calls on load: `scorecards.company` (scoped: `seats.scorecard` per visible seat), `org-chart`
// (holders and teams; shared with the chart and the sidebar), `org-chart/kpi-visibility`,
// `scorecards.teams` (team groups, when the org chart has teams), and `metrics.list` (managers)
// or `me/numbers` (a holder): which cells take a number. Every `kpi` part is behind
// `useFeature("kpi")` (ADR-0016).
// TEAM-KPI (founder-approved 2026-10-21): below the grid, "Team scorecards" — every team the
// caller may view that has KPIs, each row expanding to its team scorecard, where its lead or a
// manager adds and removes the team's KPIs (the grid's team rows are read-only).
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  buttonVariants,
  Label,
  LensBandDot,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  StatGrid,
  StatTile,
  Toggle,
  Toolbar,
  ViewTabs,
} from "@companyos/ui";
import { queryOptions, useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  BookCheckIcon,
  CalendarCheckIcon,
  ChevronRightIcon,
  FileTextIcon,
  GitForkIcon,
  HashIcon,
  Rows3Icon,
  TriangleAlertIcon,
  UserIcon,
} from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { Scorecard } from "../../api/types";
import { keepFocus, useFocus } from "../../focus/focus";
import { currentWeek, weekRangeLabel } from "../../focus/weeks";
import { scopeTeam, teamSeats } from "../chart/chart-model";
import { kpiVisibilityQuery } from "../chart/chart-queries";
import { type CompanyAccess, isManager } from "../company";
import { companyPeriodNow } from "../entry/calendar";
import { formatHours, formatMoney, formatScore, seatHolderLabel } from "../format";
import { holdersOf, kpiTile, myMembershipId, orgChartQuery } from "../home/home-queries";
import { teamKpiKeys, teamScoresQuery } from "../kpi/team-queries";
import { healthCounts, TeamScorecardCard, teamBandTone } from "../kpi/team-scorecard";
import { keys, metricsQuery, myNumbersQuery, seatPeriodScorecardQuery } from "../queries";
import { Loading, ProvisionalBadge, usePeriod } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import { PublicationsScreen } from "../structure/publications-screen";
import { useFeature, useSession } from "../use-feature";
import { ReportsList } from "./reports-list";
import { type GridGroup, type GridSeat, ScorecardGrid } from "./scorecard-grid";
import {
  branchOf,
  childOf,
  type DefInfo,
  defInfo,
  GRAIN_OPTIONS,
  type Grain,
  gridCaption,
  groupByTeam,
  keepSeat,
} from "./scorecard-model";
import type { ScorecardSearch } from "./scorecard-search";

/** UX-A: Published (the frozen weeks) is a tab here, no longer its own page. */
export type CompanyViewTab = "grid" | "reports" | "published";

/** The company card for a period of any grain (the week's keeps the shared week key). */
const companyCardQuery = (api: Api, co: string, grain: Grain, period: string) =>
  queryOptions({
    queryKey:
      grain === "week"
        ? (keys.companyScorecard(co, period) as readonly string[])
        : ([...keys.companyScorecard(co, period), grain] as readonly string[]),
    queryFn: () =>
      api.client.scorecards.company({ params: { co }, query: { period_type: grain, period } }),
  });

/** Every viewable team's score for the period (`scorecards.teams`; one call for every group). */
// The week's shares `teamScoresQuery`'s key (and its answer: a refused caller has no teams).
const teamScoresFor = (api: Api, co: string, grain: Grain, period: string) =>
  queryOptions({
    queryKey:
      grain === "week"
        ? (teamKpiKeys.scores(co, period) as readonly string[])
        : ([...teamKpiKeys.scores(co, period), grain] as readonly string[]),
    queryFn: async () => {
      try {
        return (
          await api.client.scorecards.teams({
            params: { co },
            query: { period_type: grain, period },
          })
        ).data;
      } catch (e) {
        if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return [];
        throw e;
      }
    },
  });

export function CompanyView({
  api,
  company,
  view,
  onView,
  filters = {},
  onFilters = () => {},
  scoped = false,
}: {
  api: Api;
  company: CompanyAccess;
  view: CompanyViewTab;
  onView: (view: CompanyViewTab) => void;
  /** KPIS-MODE: the grid's filters and columns, kept in the URL (scorecard-search.ts). */
  filters?: ScorecardSearch;
  onFilters?: (next: Partial<Record<keyof ScorecardSearch, unknown>>) => void;
  /**
   * A plain member (not a company KPI reader): the grid of the seats it may see, from one
   * `seats.scorecard` each; no company tiles, Reports or team scorecards.
   */
  scoped?: boolean;
}) {
  const kpi = useFeature("kpi");
  const { me } = useSession();
  const week = usePeriod();
  const { focus, setFocus } = useFocus();
  const manager = isManager(company);
  const writable = kpi.enabled && !kpi.readOnly;
  const grain: Grain = filters.cols ?? "week";
  const period = grain === "week" ? week : companyPeriodNow(company.calendar, grain, new Date());
  const current = grain === "week" ? currentWeek() : period;
  const showGrid = kpi.enabled && (scoped || view === "grid");

  const card = useQuery({
    ...companyCardQuery(api, company.id, grain, period),
    enabled: kpi.enabled && !scoped,
  });
  const chart = useQuery({ ...orgChartQuery(api, company.id), retry: false });
  const visibility = useQuery({
    ...kpiVisibilityQuery(api, company.id),
    retry: false,
    enabled: kpi.enabled,
  });
  // A closed seat stays in the visibility list (its history is still readable) but has no
  // row on the grid, as on the chart and the sidebar tree.
  const closed = new Set(
    (chart.data?.seats ?? []).filter((s) => s.status === "closed").map((s) => s.id),
  );
  const visible = visibility.data
    ? new Set(visibility.data.kpi_visible_seat_ids.filter((id) => !closed.has(id)))
    : null;
  const seatCards = useQueries({
    queries:
      scoped && kpi.enabled
        ? [...(visible ?? [])].map((id) => ({
            ...seatPeriodScorecardQuery(api, company.id, id, grain, period),
            retry: false,
          }))
        : [],
  });
  const teams = chart.data?.teams ?? [];
  const teamScores = useQuery({
    ...teamScoresFor(api, company.id, grain, period),
    enabled: showGrid && teams.length > 0,
  });
  const holders = holdersOf(chart.data);
  const mine = myMembershipId(me, company);
  const held = new Set(
    (chart.data?.seats ?? [])
      .filter((s) => s.holders.some((h) => h.membership_id === mine))
      .map((s) => s.id),
  );
  // Which cells take a number: managers read the definitions, a holder its own `me/numbers`.
  const metrics = useInfiniteQuery({
    ...metricsQuery(api, company.id),
    enabled: writable && manager && showGrid,
  });
  const numbers = useQuery({
    ...myNumbersQuery(api, company.id),
    enabled: writable && !manager && held.size > 0 && showGrid,
  });
  const defs = new Map<string, DefInfo>();
  for (const d of metrics.data?.pages.flatMap((p) => p.data) ?? []) defs.set(d.id, defInfo(d));
  for (const s of numbers.data?.seats ?? [])
    for (const i of s.items)
      defs.set(i.metric_def_id, { source: i.source, cadence: i.cadence, archived: false });
  const canEditSeat = (seat: string) => writable && (manager || held.has(seat));

  const children = scoped
    ? seatCards.flatMap((q) => (q.data ? [childOf(q.data)] : []))
    : (card.data?.children ?? []);
  // Only the seats the server lets the viewer see (a refused read, a client's, cuts nothing).
  const all: GridSeat[] = children
    .filter((c) => !visible || visible.has(c.subject.id))
    .map((c) => ({ child: c, holders: holders.get(c.subject.id) ?? [] }));

  // The seat in focus: a seat id as is; a DE through the seat it holds.
  const focusedSeat =
    focus.subject?.kind === "seat"
      ? focus.subject.id
      : focus.subject?.kind === "de"
        ? ([...holders].find(([, hs]) =>
            hs.some((h) => h.membership_id === (focus.subject as { id: string }).id),
          )?.[0] ?? null)
        : null;
  const teamId = scopeTeam(focus.scope);
  const inTeam = teamId && chart.data ? teamSeats(chart.data, teamId) : null;
  const branch = focusedSeat
    ? filters.branch && chart.data
      ? branchOf(focusedSeat, chart.data.seats)
      : new Set([focusedSeat])
    : null;
  const seats = all.filter((s) =>
    keepSeat(s.child.subject.id, [branch, inTeam, filters.mine ? held : null]),
  );

  // Groups: the company's own KPIs, then teams A–Z (teams with team KPIs stay when unfiltered),
  // then the seats in no team.
  const scores = new Map((teamScores.data ?? []).map((t) => [t.team_id, t]));
  const narrowed = Boolean(focusedSeat || filters.mine);
  const keep = new Set(
    narrowed
      ? []
      : (teamScores.data ?? [])
          .filter((t) => t.metrics > 0 && (!teamId || inTeamTree(teamId, t.team_id, teams)))
          .map((t) => t.team_id),
  );
  const byTeam = groupByTeam(
    seats,
    (s) => s.child.subject.id,
    teams,
    chart.data?.team_members ?? [],
    keep,
  );
  const companyMetrics = !scoped && !narrowed && !teamId ? (card.data?.metrics ?? []) : [];
  const groups: GridGroup[] = [
    ...(companyMetrics.length
      ? [
          {
            key: "company",
            kind: "company" as const,
            team: null,
            seats: [],
            metrics: companyMetrics,
          },
        ]
      : []),
    ...byTeam.map((g) => {
      const score = g.team ? scores.get(g.team.id) : undefined;
      return {
        key: g.team?.id ?? "none",
        kind: g.team ? ("team" as const) : ("none" as const),
        team: g.team,
        seats: g.seats,
        ...(score ? { score } : {}),
      };
    }),
  ];
  // The Team filter's choices: teams with a seat that has KPIs, or KPIs of their own.
  const kpiSeatIds = new Set(
    all.filter((s) => (s.child.metrics ?? []).length > 0).map((s) => s.child.subject.id),
  );
  const members = chart.data?.team_members ?? [];
  const teamChoices = [...teams]
    .filter(
      (t) =>
        (scores.get(t.id)?.metrics ?? 0) > 0 ||
        members.some((m) => m.team_id === t.id && kpiSeatIds.has(m.seat_id)),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const names = new Map(
    (chart.data?.seats ?? []).flatMap((s) =>
      s.holders.map((h) => [h.membership_id, h.display_name] as const),
    ),
  );

  const pending = scoped
    ? visibility.isPending || seatCards.some((q) => q.isPending)
    : card.isPending;
  const failed = scoped ? visibility.error : card.error;
  const currency = scoped
    ? (numbers.data?.currency ?? "USD")
    : (card.data?.cost?.currency ?? "USD");
  const anyEditable = all.some((s) => canEditSeat(s.child.subject.id));
  const teamSelectId = useId();
  const seatSelectId = useId();

  const grid = (
    <div className="flex min-w-0 flex-col gap-3" data-slot="weekly-grid">
      <Toolbar data-slot="scorecard-filters" className="flex-wrap">
        {teamChoices.length ? (
          <>
            <Label htmlFor={teamSelectId} className="text-muted-foreground">
              Team
            </Label>
            <NativeSelect
              id={teamSelectId}
              size="sm"
              className="w-auto max-w-60"
              value={teamId ?? ""}
              onChange={(e) =>
                setFocus({ scope: e.target.value ? `team:${e.target.value}` : "company" })
              }
            >
              <NativeSelectOption value="">All teams</NativeSelectOption>
              {teamChoices.map((t) => (
                <NativeSelectOption key={t.id} value={t.id}>
                  {t.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </>
        ) : null}
        <Label htmlFor={seatSelectId} className="text-muted-foreground">
          Seat
        </Label>
        <NativeSelect
          id={seatSelectId}
          size="sm"
          className="w-auto max-w-80"
          value={focusedSeat ?? ""}
          onChange={(e) =>
            setFocus({ subject: e.target.value ? { kind: "seat", id: e.target.value } : null })
          }
        >
          <NativeSelectOption value="">All seats ({all.length})</NativeSelectOption>
          {all.map((s) => (
            <NativeSelectOption key={s.child.subject.id} value={s.child.subject.id}>
              {seatHolderLabel(s.child.subject.name, s.holders[0]?.name)}
            </NativeSelectOption>
          ))}
        </NativeSelect>
        {focusedSeat ? (
          <Toggle
            variant="chip"
            size="sm"
            pressed={Boolean(filters.branch)}
            onPressedChange={(on) => onFilters({ branch: on || undefined })}
            data-slot="filter-branch"
          >
            <GitForkIcon data-icon="inline-start" aria-hidden="true" />
            With their branch
          </Toggle>
        ) : null}
        {held.size ? (
          <Toggle
            variant="chip"
            size="sm"
            pressed={Boolean(filters.mine)}
            onPressedChange={(on) => onFilters({ mine: on || undefined })}
            data-slot="filter-mine"
          >
            <UserIcon data-icon="inline-start" aria-hidden="true" />
            Mine
          </Toggle>
        ) : null}
        <Toggle
          variant="chip"
          size="sm"
          pressed={Boolean(filters.off)}
          onPressedChange={(on) => onFilters({ off: on || undefined })}
          data-slot="filter-off"
        >
          <TriangleAlertIcon data-icon="inline-start" aria-hidden="true" />
          Off track
        </Toggle>
        <Segmented<Grain>
          aria-label="Columns"
          size="sm"
          options={GRAIN_OPTIONS}
          value={grain}
          onValueChange={(g) => onFilters({ cols: g === "week" ? undefined : g })}
        />
        <span className="ml-auto text-xs text-muted-foreground max-lg:hidden">
          {gridCaption(grain, anyEditable)}
        </span>
      </Toolbar>
      {pending ? (
        <Loading label="Loading the scorecard grid" rows={6} />
      ) : failed ? (
        scoped ? (
          <ErrorNotice what="The scorecard" error={failed} />
        ) : null
      ) : (
        <ScorecardGrid
          api={api}
          company={company}
          grain={grain}
          period={period}
          current={current}
          groups={groups}
          currency={currency}
          canEditSeat={canEditSeat}
          manager={manager}
          defs={defs}
          offOnly={Boolean(filters.off)}
          focusKpi={filters.kpi ?? null}
          focusSeat={focusedSeat}
          names={names}
          canSetup={manager && writable}
        />
      )}
    </div>
  );

  return (
    <div
      className="flex min-w-0 flex-col"
      data-slot="company-view"
      data-scoped={scoped || undefined}
    >
      <LocationHeader
        icon={CalendarCheckIcon}
        title="Scorecard"
        badge={
          // Phones keep the title: the week is in the chips row anyway.
          <span className="flex items-center gap-1 max-sm:hidden">
            <Badge variant="neutral">
              W{week.slice(6)} · {weekRangeLabel(week)}
            </Badge>
            {kpi.enabled && !scoped && card.data ? (
              <ProvisionalBadge completeness={card.data.completeness} band={card.data.band} />
            ) : null}
          </span>
        }
        actions={
          <>
            {kpi.enabled && !scoped ? (
              <Link
                to="/c/$slug/kpis/reports/$week"
                params={{ slug: company.slug, week }}
                search={(prev) => keepFocus(prev as Record<string, unknown>)}
                className={buttonVariants({ variant: "ghost", size: "sm" })}
              >
                <FileTextIcon data-icon="inline-start" />
                <span className="max-sm:sr-only">Report</span>
              </Link>
            ) : null}
            {writable && (manager || held.size > 0) ? (
              <Link
                to={manager ? "/c/$slug/kpis/entry/numbers" : "/c/$slug/kpis/my-numbers"}
                params={{ slug: company.slug }}
                search={(prev) => keepFocus(prev as Record<string, unknown>)}
                className={buttonVariants({ variant: "secondary", size: "sm" })}
              >
                <HashIcon data-icon="inline-start" />
                <span className="max-sm:sr-only">Enter numbers</span>
              </Link>
            ) : null}
          </>
        }
      />
      <div className="flex min-w-0 flex-col px-6 pb-10 max-md:px-4">
        {kpi.enabled && !scoped ? <HeaderTiles card={card.data} pending={card.isPending} /> : null}
        {kpi.enabled && !scoped && card.isError ? (
          <ErrorNotice what="The scorecard" error={card.error} />
        ) : null}
        {kpi.enabled ? (
          <section aria-labelledby="weekly-heading" className="flex min-w-0 flex-col">
            <h2 id="weekly-heading" className="sr-only">
              Scorecard
            </h2>
            {scoped ? (
              <div className="min-w-0 pt-4">{grid}</div>
            ) : (
              <>
                <ViewTabs
                  aria-label="Scorecard views"
                  value={view}
                  onValueChange={(v) => onView(v as CompanyViewTab)}
                  tabs={[
                    { value: "grid", label: "Scorecard", icon: Rows3Icon },
                    { value: "reports", label: "Reports", icon: FileTextIcon },
                    { value: "published", label: "Published", icon: BookCheckIcon },
                  ]}
                />
                <div className="min-w-0 pt-3">
                  {view === "reports" ? (
                    <ReportsList api={api} company={company} />
                  ) : view === "published" ? (
                    <PublicationsScreen api={api} company={company} />
                  ) : (
                    grid
                  )}
                </div>
              </>
            )}
          </section>
        ) : null}
        {kpi.enabled && !scoped && view === "grid" && teams.length > 0 ? (
          <TeamScorecards api={api} company={company} week={week} />
        ) : null}
      </div>
    </div>
  );
}

/** Whether `team` is `root` or under it (the Team filter keeps its sub-teams' groups). */
function inTeamTree(
  root: string,
  team: string,
  teams: readonly { id: string; parent_team_id: string | null }[],
): boolean {
  const parent = new Map(teams.map((t) => [t.id, t.parent_team_id]));
  const seen = new Set<string>();
  for (let t: string | null | undefined = team; t && !seen.has(t); t = parent.get(t)) {
    if (t === root) return true;
    seen.add(t);
  }
  return false;
}

/**
 * The team scorecards: a disclosure (shown when the org chart, already loaded, has teams) that,
 * opened, lists one expandable row per team with KPIs — the team scorecard where its lead or a
 * manager adds and removes the team's KPIs.
 */
function TeamScorecards({
  api,
  company,
  week,
}: {
  api: Api;
  company: CompanyAccess;
  week: string;
}) {
  const [shown, setShown] = useState(false);
  const scores = useQuery({ ...teamScoresQuery(api, company.id, week), enabled: shown });
  const [open, setOpen] = useState<string | null>(null);
  const rows = scores.data ?? [];
  return (
    <section
      aria-labelledby="team-scorecards-heading"
      className="mt-8 flex min-w-0 flex-col gap-2"
      data-slot="team-scorecards"
    >
      <h2 id="team-scorecards-heading" className="text-sm font-semibold">
        <button
          type="button"
          aria-expanded={shown}
          onClick={() => setShown(!shown)}
          className="flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          <ChevronRightIcon
            aria-hidden="true"
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${shown ? "rotate-90" : ""}`}
          />
          Team scorecards
        </button>
      </h2>
      {!shown ? null : scores.isError ? (
        <ErrorNotice what="The team scorecards" error={scores.error} />
      ) : scores.isPending ? (
        <Loading label="Loading the team scorecards" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-slot="team-scorecards-empty">
          No team you can see has KPIs yet.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
          {rows.map((t) => (
            <li key={t.team_id} className="flex flex-col" data-team={t.team_id}>
              <button
                type="button"
                aria-expanded={open === t.team_id}
                onClick={() => setOpen(open === t.team_id ? null : t.team_id)}
                className="flex min-w-0 items-center gap-3 px-3 py-2 text-left text-sm hover:bg-accent focus-visible:outline-2 focus-visible:outline-ring"
              >
                <ChevronRightIcon
                  aria-hidden="true"
                  className={`size-4 shrink-0 text-muted-foreground transition-transform ${open === t.team_id ? "rotate-90" : ""}`}
                />
                <LensBandDot band={teamBandTone(t)} />
                <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
                <span className="shrink-0 tabular-nums">{formatScore(t.kpi_score)}</span>
                <span className="shrink-0 text-xs text-muted-foreground max-sm:hidden">
                  {healthCounts(t)}
                </span>
              </button>
              {open === t.team_id ? (
                <div className="border-t border-border px-3 py-3">
                  <TeamScorecardCard
                    api={api}
                    company={company}
                    team={{ id: t.team_id, name: t.name }}
                    week={week}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function HeaderTiles({ card, pending }: { card: Scorecard | undefined; pending: boolean }) {
  const dash = pending ? "…" : "—";
  const currency = card?.cost?.currency ?? "USD";
  const founder = card?.hours?.founder_minutes;
  return (
    <StatGrid data-slot="company-tiles" className="my-4">
      <StatTile
        label="KPI score"
        value={card ? formatScore(card.kpi_score) : dash}
        {...(card ? kpiTile(card) : {})}
      />
      <StatTile
        label="Goal score"
        value={card ? formatScore(card.goal_score) : dash}
        detail={card ? `${card.children?.length ?? 0} seats scored` : undefined}
      />
      <StatTile
        label="Cost"
        value={card?.cost ? formatMoney(card.cost.total_minor, currency) : dash}
        detail={card && !card.cost ? "No DE costs" : undefined}
      />
      <StatTile
        label="Supervisor hours"
        value={card?.hours ? formatHours(card.hours.supervision_minutes) : dash}
        detail={
          founder !== undefined && founder !== null
            ? `Founder ${formatHours(founder)} (agency only)`
            : card && !card.hours
              ? "No DE to supervise"
              : undefined
        }
      />
    </StatGrid>
  );
}
